import { formatCurrency, formatPercent } from '@angular/common';
import { Injectable } from '@angular/core';
import { RegionVariantService } from 'app/core/region-variant/region-variant.service';

/** Formats numbers according to current locale */
@Injectable({ providedIn: 'root' })
export class NumberFormatter {
  constructor(private regionVariantService: RegionVariantService) {}

  /** Return formatted value, e.g. "10.00" */
  format(
    value: number,
    maximumFractionDigits: number,
    minimumFractionDigits: number = 0,
    useGrouping: boolean = true,
  ): string {
    const locale = this.regionVariantService.getLocalisation();
    return (
      value &&
      value.toLocaleString(locale, {
        minimumFractionDigits,
        maximumFractionDigits,
        useGrouping,
      })
    );
  }

  /** Return formatted percent from decimal value, e.g.  0.1 -> "10.00%" */
  formatPercent(value: number, digitsInfo = '1.2-2'): string {
    const locale = this.regionVariantService.getLocalisation();
    return value !== null && formatPercent(value, locale, digitsInfo);
  }

  /** Return formatted currency value, with or without cents */
  formatCurrency(value: number, showCents: boolean = true, excludeCurrencySymbol: boolean = false): string {
    const locale = this.regionVariantService.getLocalisation();
    const currencySymbol = excludeCurrencySymbol ? '' : this.regionVariantService.getCurrencySymbol();
    const currencyCode = this.regionVariantService.getCurrencyCode();
    return formatCurrency(value, locale, currencySymbol, currencyCode, showCents ? '1.2-2' : '1.0-0');
  }

  /** Parse localised number, handling thousands & decimal separators. Returns null if not a valid number. */
  private parserCache: Record<string, { groupSeparator: string; decimalSeparator: string }> = {};
  parse(value: string): number {
    if (!value) return null;

    const locale = this.regionVariantService.getLocalisation();

    let parserDetails = this.parserCache[locale];
    if (!parserDetails) {
      const formatter = Intl.NumberFormat(locale);
      const parts = formatter.formatToParts(1000.2);
      let groupSeparator = parts.filter((p) => p.type === 'group')[0]?.value;
      let decimalSeparator = parts.filter((p) => p.type === 'decimal')[0]?.value;

      if (decimalSeparator === '.') decimalSeparator = null;

      parserDetails = {
        groupSeparator,
        decimalSeparator,
      };
      this.parserCache[locale] = parserDetails;
    }

    if (!value) null;

    if (parserDetails.groupSeparator) value = value.replaceAll(parserDetails.groupSeparator, '');
    if (parserDetails.decimalSeparator) value = value.replaceAll(parserDetails.decimalSeparator, '.');

    const numberValue = parseFloat(value);
    return !isNaN(numberValue) && isFinite(numberValue) ? numberValue : null;
  }
}
